import type {
  PrintConfig,
  ReceiptData,
  ReceiptItemLine,
  ReceiptPaymentLine,
  ReceiptCustomer,
} from '@matix/types';

/**
 * Inputs to `buildReceiptData`. Intentionally weakly typed at the boundary —
 * each call site (PaymentModal success, future reprint flow) has different
 * upstream types, so we accept a structural minimum here and let TS narrow at
 * the caller.
 */
export interface BuildReceiptDataInput {
  printConfig: PrintConfig;

  /** What POST /api/v1/orders returned. */
  orderResponse: {
    order_id: number;
    /** Backend may or may not return the customer-facing Code today; both are tolerated. */
    code?: number | string | null;
    entry_date: string;
    /** Server-authoritative change due (POST response should contain this). */
    change_due?: number | null;
    /**
     * `Inv_Receipt_h.PrintCount` after the write — 1 for a new sale, higher
     * when this response came from an EDIT. That is the whole reason it is
     * read from the response rather than assumed to be 1 here: the cashier's
     * own copy of an edited receipt has to carry the same number the reprint
     * of it will.
     */
    print_count?: number | null;
    /** Who just saved this receipt as an EDIT, and when. Absent on a new sale. */
    last_edit?: { at: string; by_name: string | null; by_name_ar?: string | null } | null;
    /**
     * Automatic promotions the server applied, named. Their money is already
     * inside `totals.discount`; these only explain it on the printout.
     */
    promotions?: Array<{ promotion_id: number; name: string; name_ar?: string | null; discount_amount: number; level?: 'line' | 'order' }>;
  };

  /** Items as they were on the cart at charge time. */
  items: Array<{
    name: string;
    nameAr?: string | null;
    /** Item code / item number — printed when ShowItemNoInPrint. */
    code?: string | null;
    /** Item barcode — printed when DisplayItemBarcodeinPOSInvoice. */
    barcode?: string | null;
    /**
     * The unit the line sold in, both scripts. Set only on a multi-unit item —
     * the caller already applies that rule, and the reprint path carries the
     * same two fields, so a design printing "3 Meal" prints it on the first
     * copy and on every reprint.
     */
    unit?: string | null;
    unitAr?: string | null;
    /** Per-line note — printed when IsPrintCustomOptions. */
    note?: string | null;
    /** Selected modifiers — printed when IsPrintCustomOptions. */
    mods?: string[];
    /** The line's salesman by display name, each script — printed when set. */
    salesman?: string | null;
    salesmanAr?: string | null;
    qty: number;
    price: number;
    discount?: number;
    total: number;
    isReturn?: boolean;
  }>;

  totals: {
    subtotal: number;
    discount: number;
    /** Delivery fee, when the order carried one. Its own header column — not
     *  part of the subtotal. Omit or 0 on an order without delivery. */
    deliveryFee?: number;
    tax: number;
    total: number;
  };

  /** Present on a return/exchange — drives the grouped Refund/Invoice/Net printout. */
  exchange?: ReceiptData['exchange'];

  /**
   * Payment splits as sent in the POST body, plus a localized label resolved
   * from the `paymentTypeMap` the cashier app already loads at boot.
   */
  payments: Array<{
    typeName: string;
    typeNameAr?: string | null;
    amount: number;
    isCash?: boolean;
  }>;

  /** Sum of cash-flagged splits (informational on the receipt). */
  cashTendered?: number;

  cashier: {
    id: number;
    /**
     * The person's REAL name — `Security_Users.Name`, not `LoginName`. The
     * receipt used to print whatever the cashier types to log in ("afathy"),
     * which is not who the customer spoke to.
     */
    name?: string | null;
    /** `Security_Users.Name_AR`. Printed on an Arabic slip; see cashierNameOf. */
    nameAr?: string | null;
  };

  /** Attached customer at charge time; absent for walk-in cash sales. */
  customer?: ReceiptCustomer | null;

  /**
   * The order's VAT rate as a percentage. Only the A4 tax invoice uses it — to
   * print a per-line tax column and label its VAT row — and it falls back to
   * deriving the rate from the totals when omitted.
   */
  taxPercent?: number | null;

  /** Override the default 'EGP' if the tenant operates in a different currency. */
  currency?: string;

  /**
   * Pre-formatted table line ("Sun bed: 5" / "Table: 7"). When present,
   * the receipt template renders it under the cashier metadata. Omit
   * for walk-in / takeaway receipts with no table attached.
   */
  tableLabel?: string | null;
  /**
   * The receipt's memo as stored on it. At charge time the server writes the
   * order's reference number into `Inv_Receipt_h.Memo`, so pass that here and
   * the first print matches every reprint.
   */
  memo?: string | null;
  /** The till's "Reference no." box — the same value as `memo`. */
  referenceNo?: string | null;
  /** The till's "Notes" box — free text typed at payment. */
  notes?: string | null;
  /**
   * Delivery orders only: the promised delivery time (naive tenant wall-clock,
   * same shape as `entry_date`). Printed under the date. Omit otherwise.
   */
  deliveryDate?: string | null;

  /**
   * The order type the sale was charged as — `CreateOrderRequest.order_type`.
   * Only a custom design prints it (`{{order_type}}`), and it has to come from
   * the request rather than be inferred, so the first copy and every reprint
   * head the ticket with the same word.
   */
  orderType?: number | null;
}

/**
 * Translate live cashier-app state into the `ReceiptData` shape consumed by
 * the receipt template. Pure function — no DOM access, no I/O — so it can be
 * unit-tested directly.
 *
 * The function is forgiving: missing optional fields fall back to sensible
 * defaults rather than throwing. The receipt should always print, even when
 * (say) a tenant hasn't filled in the company contact line.
 */
export function buildReceiptData(input: BuildReceiptDataInput): ReceiptData {
  const items: ReceiptItemLine[] = input.items.map((it) => ({
    name: it.name,
    nameAr: it.nameAr ?? null,
    code: it.code ?? null,
    barcode: it.barcode ?? null,
    // Omitted rather than nulled on a single-unit item, so a slip that never
    // named a unit is byte-identical to what it was.
    ...(it.unit || it.unitAr ? { unit: it.unit ?? null, unitAr: it.unitAr ?? null } : {}),
    note: it.note ?? null,
    ...(it.mods && it.mods.length > 0 ? { mods: it.mods } : {}),
    ...(it.salesman || it.salesmanAr
      ? { salesman: it.salesman ?? null, salesmanAr: it.salesmanAr ?? null }
      : {}),
    qty: it.qty,
    price: it.price,
    discount: it.discount,
    total: it.total,
    ...(it.isReturn ? { isReturn: true } : {}),
  }));

  const payments: ReceiptPaymentLine[] = input.payments.map((p) => ({
    typeName: p.typeName,
    typeNameAr: p.typeNameAr ?? null,
    amount: p.amount,
    isCash: p.isCash,
  }));

  // Prefer the server's change_due (authoritative). Fall back to the local
  // calculation only when the server didn't send one — the server has the
  // canonical view of what was actually applied to the GL voucher.
  const changeDue =
    typeof input.orderResponse.change_due === 'number'
      ? input.orderResponse.change_due
      : input.cashTendered != null
      ? Math.max(0, input.cashTendered - input.totals.total)
      : undefined;

  return {
    printConfig: input.printConfig,
    orderId: input.orderResponse.order_id,
    orderCode: input.orderResponse.code ?? null,
    entryDate: input.orderResponse.entry_date,
    cashier: input.cashier,
    customer: input.customer ?? undefined,
    ...(input.taxPercent != null ? { taxPercent: input.taxPercent } : {}),
    items,
    totals: input.totals,
    payments,
    cashTendered: input.cashTendered,
    changeDue,
    currency: input.currency,
    tableLabel: input.tableLabel ?? null,
    memo: input.memo ?? null,
    referenceNo: input.referenceNo ?? input.memo ?? null,
    notes: input.notes ?? null,
    deliveryDate: input.deliveryDate ?? null,
    printCount: input.orderResponse.print_count ?? null,
    editedBy: input.orderResponse.last_edit?.by_name ?? null,
    editedByAr: input.orderResponse.last_edit?.by_name_ar ?? null,
    editedAt: input.orderResponse.last_edit?.at ?? null,
    orderType: input.orderType ?? null,
    ...(input.exchange ? { exchange: input.exchange } : {}),
    // Omitted entirely when the order matched none, so a tenant without
    // promotions gets a byte-identical receipt to the one it prints today.
    ...(input.orderResponse.promotions && input.orderResponse.promotions.length > 0
      ? {
          promotions: input.orderResponse.promotions.map((p) => ({
            name: p.name,
            ...(p.name_ar ? { nameAr: p.name_ar } : {}),
            amount: p.discount_amount,
            ...(p.level ? { level: p.level } : {}),
          })),
        }
      : {}),
  };
}
